// @flow

import React, { type Node, useEffect } from 'react';

import { getDocumentTypeFromFormattedValue } from '@tectonics/prs/customer/brazil';
import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/normalize';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError } from '@tectonics/prs/validation';

import { Input, Label } from '../../../../../commons/atoms';
import { Field } from '../../../../../commons/molecules';
import { ValidationErrorMessage } from './validation-error-message';

export function FieldBrazilianDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    'brazilianDocument',
    formatRaw,
    normalizeCustomerDocument
  );

  const [, setDocumentType] = useUserInputValue('brazilianDocumentType');

  useEffect(() => {
    setDocumentType(getDocumentTypeFromFormattedValue(document));
  }, [document, setDocumentType]);

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('brazilianDocument');

  return (
    <Field
      data-test-name="customer-document"
      label={<Label htmlFor={inputId}>CPF ou CNPJ</Label>}
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={formHasBeenSent && !!validationError}
    >
      <Input
        as={InputMask}
        id={inputId}
        value={document}
        onChange={handleChangeDocument}
        mask={maskFunction}
        readOnly={submitting}
        placeholder="Insira seu CPF ou CNPJ"
        type="tel"
        name="customer-document"
      />
    </Field>
  );
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, maskRawFunction);
}

function maskFunction(value: string): ArrayMask {
  return value.length <= 14 ? CPF_MASK : CNPJ_MASK;
}

function maskRawFunction(rawValue: string): ArrayMask {
  return rawValue.length <= 11 ? CPF_MASK : CNPJ_MASK;
}

const CPF_MASK = [
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '.',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '.',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '-',
  /[0-9]/,
  /[0-9]/,
];

const CNPJ_MASK = [
  /[0-9]/,
  /[0-9]/,
  '.',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '.',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '/',
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  /[0-9]/,
  '-',
  /[0-9]/,
  /[0-9]/,
];
